/**
 * A Goethe-Zertifikat sitting (D-118) in focus mode, driven by the server state: the modules of
 * the level in the official order, each with its official time.
 * - Hören: one document at a time. Before the first text of a part: its instruction and its
 *   example (with the solution). Reading time, then the recording (the part's instruction, the
 *   example and the text, heard once or twice as the part says: the plays are in the audio
 *   itself), then the answer time; never back to a previous document. Training mode: a full
 *   player instead (pause, back, listen again at will), nothing starts by itself (D-144).
 * - Lesen: the parts with their texts, reserve and example; free navigation (C2: the parts in the
 *   imposed order, never back).
 * - Schreiben and Sprechen: GoetheWriting, GoetheSpeaking.
 */

import {
  academyAttemptsModuleCreate,
  academyAttemptsNextCreate,
  academyAttemptsPartCreate,
  academyAttemptsPassageAudioRetrieve,
  ApiError,
  useAcademyAttemptsRetrieve,
} from "@agencyos/api-client";
import { GOETHE_MODULES, type GoetheSitting, type SpokenLine, listeningPhase, parseGoetheSitting, playsCount, readingSecondsFor } from "@agencyos/core";
import { Badge, Button, Skeleton } from "@agencyos/ui-web";
import { ArrowRight, Headphones, Play } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

import { readStorage, writeStorage } from "../../lib/storage";
import { OnceAudio, speechAvailable, stopSpeaking, unlockOnFirstGesture } from "../audio";
import { ExamAbandon, ExamAudioPanel, ExamBar, ExamCountdown, ExamFrame, ExamHeadTool, ExamNav, ExamPill, revealItem, sequenceCells, useClockTotal } from "../ExamFrame";
import { ExamPictureView } from "../ExamPicture";
import { TrainingListen, TrainingNote } from "../ListeningReplay";
import { MethodLink } from "../ielts/MethodLink";
import { speakLines, stopLines } from "../listening";
import { SectionDone } from "../McqSection";
import { useCountdown } from "../shared";
import { clock } from "../text";
import { SkillIcon } from "../ui";
import { useAcademyPaths } from "../paths";
import { GoetheExampleCard, GoetheItem, GoethePool, GoetheTexts, useGoetheAnswers } from "./GoetheItems";
import { GoetheSpeaking, GoetheWriting } from "./GoetheProductions";
import "./goethe.css";

const heardKey = (attemptId: string, passageId: string) => `academy.goethe.heard.${attemptId}.${passageId}`;
const WAVE = [0, 1, 2, 3, 4, 5, 6, 7, 8];
type Listen = "waiting" | "playing" | "blocked" | "heard";

/** Without a generated audio: the device's voices read the example and the text `plays` times. */
async function speakDocument(lines: SpokenLine[], exampleLines: SpokenLine[], plays: number): Promise<"done" | "blocked" | "stopped"> {
  const options = { lang: "de", preferred: "de-DE" };
  for (const block of [...Array.from({ length: exampleLines.length ? plays : 0 }, () => exampleLines), ...Array.from({ length: plays }, () => lines)]) {
    const outcome = await speakLines(block, options);
    if (outcome !== "done") return outcome;
    await new Promise((resolve) => window.setTimeout(resolve, 1500));
  }
  return "done";
}

function GoetheListening({ attemptId, state, onChanged }: { attemptId: string; state: GoetheSitting; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const group = state.current;
  const passage = group?.passage ?? null;
  const questions = useMemo(() => group?.questions ?? [], [group]);
  const key = passage?.id ?? `doc-${state.part}`;
  const [listen, setListen] = useState<Listen>(() => (readStorage(heardKey(attemptId, key)) === "1" ? "heard" : "waiting"));
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(Date.now());
  const [endedAt, setEndedAt] = useState<number | null>(listen === "heard" ? 0 : null);
  const [problem, setProblem] = useState(false);
  const [moving, setMoving] = useState(false);
  const audio = useRef<OnceAudio | null>(null);
  const started = useRef(listen !== "waiting");
  const closed = useRef(false);
  const initial = useMemo(() => Object.fromEntries(questions.map((q) => [q.id, q.response])), [questions]);
  const { set, flush, valueOf, error } = useGoetheAnswers(attemptId, initial);
  const training = state.mode === "training";
  // the full reading time before the first text of a part (or before each one: C1 Teil 3), a short one between two texts
  const flow = { readingSeconds: readingSecondsFor(passage, state.flow), answerSeconds: state.flow.answerSeconds };

  useEffect(() => {
    const tick = () => setElapsed(Math.floor((Date.now() - startedAt.current) / 1000));
    const timer = window.setInterval(tick, 500);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);
  useEffect(() => {
    unlockOnFirstGesture();
    return () => {
      audio.current?.stop();
      stopSpeaking();
      stopLines();
    };
  }, []);
  const elapsedRef = useRef(0);
  useEffect(() => {
    elapsedRef.current = elapsed;
  }, [elapsed]);

  const heard = useCallback(() => {
    writeStorage(heardKey(attemptId, key), "1");
    setListen("heard");
    setEndedAt((value) => value ?? elapsedRef.current);
  }, [attemptId, key]);

  const play = useCallback(async () => {
    if (started.current || !passage) return;
    started.current = true;
    writeStorage(heardKey(attemptId, key), "1"); // a reload never gives another listening
    setListen("playing");
    if (passage.hasAudio) {
      try {
        const response = await academyAttemptsPassageAudioRetrieve(attemptId, passage.id);
        const player = new OnceAudio({
          onEnded: heard,
          onFailed: () => {
            setProblem(true);
            heard();
          },
        });
        audio.current = player;
        if ((await player.start(response.data.url)) === "blocked") setListen("blocked");
      } catch {
        setProblem(true);
        heard();
      }
      return;
    }
    if (!speechAvailable() || !passage.lines.length) {
      setProblem(true);
      heard();
      return;
    }
    const outcome = await speakDocument(passage.lines, passage.first ? passage.exampleLines : [], playsCount(passage));
    if (outcome === "blocked") setListen("blocked");
    else heard();
  }, [attemptId, key, passage, heard]);

  const resume = async () => {
    setListen("playing");
    if (audio.current) {
      if ((await audio.current.resume()) === "blocked") setListen("blocked");
      return;
    }
    started.current = false;
    await play();
  };

  const next = useCallback(async () => {
    if (closed.current) return;
    closed.current = true;
    setMoving(true);
    try {
      audio.current?.stop();
      stopLines();
      await flush();
      await academyAttemptsPartCreate(attemptId);
    } catch {
      // already closed (time up): the refreshed state shows what comes next
    } finally {
      await onChanged();
      setMoving(false);
    }
  }, [attemptId, flush, onChanged]);

  const phase = listeningPhase(elapsed, flow, { started: listen !== "waiting", endedAt });
  useEffect(() => {
    if (phase === "listening" && listen === "waiting" && !training) void play();
    if (phase === "closed" && !training) void next();
  }, [phase, listen, play, next, training]);
  const passageId = passage?.id ?? "";
  const loadAudio = useCallback(
    async () => (await academyAttemptsPassageAudioRetrieve(attemptId, passageId)).data.url,
    [attemptId, passageId],
  );
  const trainingLines = useMemo(
    () => (passage ? [...(passage.first ? passage.exampleLines : []), ...passage.lines] : []),
    [passage],
  );

  if (!group || !passage) return <SectionDone onContinue={() => void next()} busy={moving} />;
  const readingLeft = Math.max(flow.readingSeconds - elapsed, 0);
  const answerLeft = endedAt === null ? flow.answerSeconds : Math.max(flow.answerSeconds - (elapsed - endedAt), 0);
  const plays = playsCount(passage);

  return (
    <div className="ac-listening ac-goethe-listening">
      <section className="ac-question__sheet ac-listening__head" aria-labelledby="goethe-doc-title">
        <div className="ac-listening__intro">
          <p className="ac-player__eyebrow ao-num">
            {t("goethe.player.teil", { n: passage.teil })} · {t("goethe.player.textOf", { n: passage.doc, total: passage.docs })}
          </p>
          <h2 id="goethe-doc-title" className="ac-title-3">
            {passage.context || t("goethe.player.text", { n: state.part })}
          </h2>
          <p className="ac-secondary" lang="de">
            {passage.instructions}
          </p>
          <Badge tone={plays > 1 ? "info" : "default"}>{t(plays > 1 ? "goethe.player.twice" : "goethe.player.once")}</Badge>
        </div>
        <ExamAudioPanel playing={!training && listen === "playing" && passage.hasAudio} note={training ? <TrainingNote /> : <p className="ac-footnote">{t("listening.training.tip")}</p>}>
        {training ? (
          <div lang="de">
            <TrainingListen
              load={passage.hasAudio ? loadAudio : null}
              lines={trainingLines}
              lang="de"
              preferred="de-DE"
              onStart={() => {
                if (listen !== "heard") heard();
              }}
            />
          </div>
        ) : (
        <div className={`ac-listen ac-listen--${listen === "heard" ? "done" : listen === "playing" ? "playing" : "idle"}`}>
          {phase === "reading" && (
            <p className="ac-status" role="timer" aria-live="polite">
              {t("goethe.player.readFirst", { time: clock(readingLeft) })}
            </p>
          )}
          {listen === "blocked" && (
            <button type="button" className="ac-listen__button" onClick={() => void resume()}>
              <span className="ac-listen__disc" aria-hidden="true">
                <Play size={30} strokeWidth={2.2} />
              </span>
              <span className="ac-listen__label">{t("academy.player.listen")}</span>
            </button>
          )}
          {listen === "playing" && (
            <div className="ac-listen__playing" role="status">
              <span className="ac-wave" aria-hidden="true">
                {WAVE.map((n) => (
                  <span key={n} style={{ animationDelay: `${n * -0.13}s` }} />
                ))}
              </span>
              <span>{t("goethe.player.playing")}</span>
            </div>
          )}
          {listen === "heard" && (
            <p className="ac-status ac-status--success" role="timer" aria-live="polite">
              <Headphones aria-hidden size={16} />
              {t("goethe.player.answerNow", { time: clock(answerLeft) })}
            </p>
          )}
          {problem && <p className="ko ac-footnote">{t("academy.player.audioProblem")}</p>}
        </div>
        )}
        </ExamAudioPanel>
      </section>

      <ExamPictureView picture={passage.image} />
      {passage.first && passage.example && <GoetheExampleCard example={passage.example} passage={passage} />}
      <GoethePool passage={passage} />

      <section className="ac-question__sheet" aria-label={t("goethe.player.items")}>
        <ol className="ac-goethe-items">
          {questions.map((question) => (
            <GoetheItem key={question.id} question={question} passage={passage} value={valueOf(question)} onChange={(choice) => set(question, choice)} />
          ))}
        </ol>
        {error && <p className="ko ac-footnote">{t("academy.player.networkError")}</p>}
        <ExamHeadTool>
          <MethodLink exam="goethe" skill="co" task={passage.teil} type={state.level} newTab compact />
        </ExamHeadTool>
      </section>

      <ExamNav
        cells={sequenceCells(
          state.total,
          questions.map((q) => ({ number: q.number, answered: valueOf(q) !== null })),
          state.answered + 1,
        )}
      />
      <ExamBar
        hint={t("goethe.player.noBack")}
        left={
          !training && phase === "reading" ? (
            <ExamCountdown seconds={readingLeft} label={t("examFrame.readingTime")} tone="reading" />
          ) : !training && listen === "heard" ? (
            <ExamCountdown seconds={answerLeft} label={t("examFrame.answerTime")} />
          ) : null
        }
        pills={
          <>
            <ExamPill>{t("goethe.player.teil", { n: passage.teil })}</ExamPill>
            {state.level && <ExamPill>{state.level}</ExamPill>}
          </>
        }
        right={
          <>
            {!training && <ExamAbandon />}
            <Button size="lg" variant={listen === "heard" ? "primary" : "secondary"} disabled={moving || listen !== "heard"} onClick={() => void next()}>
              {state.part >= state.parts ? t("goethe.player.finishListening") : t("goethe.player.nextText")}
              <ArrowRight aria-hidden size={18} />
            </Button>
          </>
        }
      />
    </div>
  );
}

function GoetheReading({ attemptId, state, onChanged }: { attemptId: string; state: GoetheSitting; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [focused, setFocused] = useState<number | null>(null); // the number chosen in the grid
  const [moving, setMoving] = useState(false);
  const all = useMemo(() => state.sections.flatMap((s) => s.groups.flatMap((g) => g.questions)), [state.sections]);
  const initial = useMemo(() => Object.fromEntries(all.map((q) => [q.id, q.response])), [all]);
  const { set, flush, valueOf, error } = useGoetheAnswers(attemptId, initial);
  const section = state.sections[state.fixedOrder ? 0 : Math.min(tab, state.sections.length - 1)];
  const answered = all.filter((q) => valueOf(q) !== null).length;
  const move = async (finish: boolean) => {
    setMoving(true);
    try {
      await flush();
      if (finish) await academyAttemptsNextCreate(attemptId);
      else await academyAttemptsPartCreate(attemptId);
    } finally {
      await onChanged();
      setMoving(false);
    }
  };
  if (!section) return <SectionDone onContinue={() => void move(true)} busy={moving} />;
  const last = state.fixedOrder ? state.part >= state.parts : true;
  return (
    <div className="ac-goethe-reading">
      {!state.fixedOrder && state.sections.length > 1 && (
        <nav className="ac-goethe-tabs" aria-label={t("goethe.player.parts")}>
          {state.sections.map((s, index) => (
            <button key={s.teil} type="button" className={`ac-goethe-tab${index === tab ? " ac-goethe-tab--on" : ""}`} aria-current={index === tab} onClick={() => setTab(index)}>
              {t("goethe.player.teil", { n: s.teil })}
            </button>
          ))}
        </nav>
      )}
      {state.fixedOrder && <p className="ac-note">{t("goethe.player.fixedOrder", { n: state.part, total: state.parts })}</p>}
      {section.groups.map((group, index) => (
        <section key={group.passage?.id ?? index} className="ac-question__sheet ac-goethe-part" aria-label={t("goethe.player.teil", { n: section.teil })}>
          {group.passage && (
            <>
              <h2 className="ac-title-3">{t("goethe.player.teil", { n: section.teil })}</h2>
              <p className="ac-secondary" lang="de">
                {group.passage.instructions}
              </p>
              {group.passage.example && <GoetheExampleCard example={group.passage.example} passage={group.passage} />}
              <div className="ac-goethe-split">
                <div className="ac-goethe-split__read">
                  <ExamPictureView picture={group.passage.image} />
                  <GoetheTexts passage={group.passage} />
                  <GoethePool passage={group.passage} />
                </div>
                <ol className="ac-goethe-items">
                  {group.questions.map((question) => (
                    <GoetheItem
                      key={question.id}
                      question={question}
                      passage={group.passage}
                      value={valueOf(question)}
                      onChange={(choice) => set(question, choice)}
                      showTexts={question.textIds.length > 1}
                    />
                  ))}
                </ol>
              </div>
            </>
          )}
          {index === 0 && (
            <ExamHeadTool>
              <MethodLink exam="goethe" skill="ce" task={section.teil} type={state.level} newTab compact />
            </ExamHeadTool>
          )}
        </section>
      ))}
      {error && <p className="ko">{t("academy.player.networkError")}</p>}
      <ExamNav
        cells={all.map((question) => {
          const home = state.sections.findIndex((s) => s.groups.some((g) => g.questions.includes(question)));
          return {
            key: question.id,
            text: String(question.number),
            state: valueOf(question) !== null ? ("done" as const) : ("empty" as const),
            current: question.number === (focused ?? section.groups[0]?.questions[0]?.number),
            // C2: the parts in the imposed order, the numbers are only shown
            onSelect: state.fixedOrder
              ? undefined
              : () => {
                  setTab(home);
                  setFocused(question.number);
                  revealItem(`goethe-item-${question.number}`);
                },
          };
        })}
      />
      <div className="ac-finish">
        <p className="ac-caption ao-num">{t("goethe.player.answered", { n: answered, total: state.total || all.length })}</p>
        {!state.fixedOrder && tab < state.sections.length - 1 && (
          <Button variant="secondary" onClick={() => setTab(tab + 1)}>
            {t("goethe.player.nextPart")} <ArrowRight aria-hidden size={16} />
          </Button>
        )}
        <Button size="lg" disabled={moving} onClick={() => void move(state.fixedOrder ? last : true)}>
          {state.fixedOrder && !last ? t("goethe.player.nextPart") : t(state.block ? "conformity.goethe.finishBlock" : "goethe.player.finishModule")}
        </Button>
      </div>
    </div>
  );
}

/** D-163: A1 Lesen and Schreiben, one block of 45 minutes; the learner goes from one to the
 * other in the order they choose, the clock of the block keeps running. */
function BlockSwitch({ attemptId, state, onChanged }: { attemptId: string; state: GoetheSitting; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const [moving, setMoving] = useState(false);
  if (!state.block) return null;
  const go = async (skill: GoetheSitting["section"]) => {
    if (!skill || skill === state.section || (skill !== "ce" && skill !== "ee")) return;
    setMoving(true);
    try {
      await academyAttemptsModuleCreate(attemptId, { skill });
    } finally {
      await onChanged();
      setMoving(false);
    }
  };
  return (
    <div className="ac-goethe-block">
      <nav className="ac-goethe-tabs" aria-label={t("conformity.goethe.block", { minutes: state.block.minutes })}>
        {state.block.skills.map((skill) => (
          <button
            key={skill}
            type="button"
            className={`ac-goethe-tab${skill === state.section ? " ac-goethe-tab--on" : ""}`}
            aria-current={skill === state.section}
            disabled={moving}
            onClick={() => void go(skill)}
          >
            {GOETHE_MODULES[skill]} · {t(`goethe.module.${skill}`)}
          </button>
        ))}
      </nav>
      <p className="ac-footnote">{t("conformity.goethe.blockNote", { minutes: state.block.minutes })}</p>
    </div>
  );
}

export function GoethePlayer({ id }: { id: string }) {
  const { t } = useTranslation();
  const paths = useAcademyPaths();
  const query = useAcademyAttemptsRetrieve(id, { query: { retry: 1, staleTime: 0, refetchOnWindowFocus: false } });
  const heading = useRef<HTMLHeadingElement>(null);
  const state = useMemo(() => (query.data?.status === 200 ? parseGoetheSitting(query.data.data) : null), [query.data]);
  const refresh = useCallback(async () => {
    await query.refetch();
  }, [query]);
  const left = useCountdown(state?.remaining ?? 0, state ? query.dataUpdatedAt : 0, () => void refresh());
  const section = state?.section ?? "";
  const clockTotal = useClockTotal(`${id}.${section}`, state?.remaining ?? 0, 0);
  useEffect(() => {
    if (section) heading.current?.focus({ preventScroll: true });
  }, [section]);

  if (query.isLoading) {
    return (
      <div className="ac-player ac-player--loading">
        <Skeleton style={{ height: 64 }} />
        <Skeleton style={{ height: 360 }} />
      </div>
    );
  }
  if (query.error instanceof ApiError && query.error.status === 404) return <Navigate to={paths.home} replace />;
  if (!state) {
    return (
      <div className="ac-surface ac-card ac-card--center">
        <p className="ko">{t("academy.player.networkError")}</p>
        <Button onClick={() => void refresh()}>{t("common.retry")}</Button>
      </div>
    );
  }
  if (state.status !== "in_progress") return <Navigate to={paths.results(id)} replace />;
  const index = state.section ? state.skills.indexOf(state.section) : 0;
  const training = state.mode === "training";

  return (
    <ExamFrame
      className={`ac-player--ielts ac-player--goethe ac-player--${state.section || "co"}`}
      icon={state.section && <SkillIcon skill={state.section} size={18} />}
      eyebrow={`${state.officialName || `Goethe-Zertifikat ${state.level}`}${state.kind ? ` · ${t(`academy.kind.${state.kind}`)}` : ""}${
        state.skills.length > 1 ? ` · ${t("academy.player.sectionOf", { n: index + 1, total: state.skills.length })}` : ""
      }`}
      title={state.section ? `${GOETHE_MODULES[state.section]} · ${t(`goethe.module.${state.section}`)}` : ""}
      headingRef={heading}
      training={training}
      seconds={left}
      clockTotal={clockTotal}
      steps={state.skills}
      stepIndex={index}
      quitNote={state.section === "co" ? t("examFrame.noteListening") : state.section === "eo" ? t("examFrame.noteRecording") : undefined}
      footnote={t("goethe.player.estimate")}
    >
      <BlockSwitch attemptId={id} state={state} onChanged={refresh} />
      {state.section === "co" && <GoetheListening key={`co-${id}-${state.part}`} attemptId={id} state={state} onChanged={refresh} />}
      {state.section === "ce" && <GoetheReading key={`ce-${id}-${state.part}`} attemptId={id} state={state} onChanged={refresh} />}
      {state.section === "ee" && <GoetheWriting key={`ee-${id}`} attemptId={id} state={state} onChanged={refresh} />}
      {state.section === "eo" && <GoetheSpeaking key={`eo-${id}`} attemptId={id} state={state} onChanged={refresh} />}
    </ExamFrame>
  );
}
